import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getShortcuts,
  subscribeToShortcuts,
  type ShortcutDescriptor,
} from "@/lib/shortcut-registry";

function useShortcuts(): ShortcutDescriptor[] {
  return useSyncExternalStore(subscribeToShortcuts, getShortcuts, getShortcuts);
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if ((target as HTMLElement).isContentEditable) return true;
  if (target.closest('[contenteditable="true"]')) return true;
  return false;
}

function hasOpenOverlay(): boolean {
  if (typeof document === "undefined") return false;
  return Boolean(
    document.querySelector(
      '[role="dialog"][data-state="open"], [cmdk-root] [cmdk-input]:focus, [role="listbox"][data-state="open"]',
    ),
  );
}

export function ShortcutHelpOverlay() {
  const [open, setOpen] = useState(false);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const shortcuts = useShortcuts();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "?") return;
      if (event.defaultPrevented) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      if (hasOpenOverlay()) return;
      event.preventDefault();
      previouslyFocusedRef.current =
        (document.activeElement as HTMLElement | null) ?? null;
      setOpen(true);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, ShortcutDescriptor[]>();
    for (const s of shortcuts) {
      const arr = map.get(s.group) ?? [];
      arr.push(s);
      map.set(s.group, arr);
    }
    return Array.from(map.entries())
      .map(([group, items]) => ({
        group,
        items: items.slice().sort((a, b) => a.key.localeCompare(b.key)),
      }))
      .sort((a, b) => a.group.localeCompare(b.group));
  }, [shortcuts]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      const el = previouslyFocusedRef.current;
      previouslyFocusedRef.current = null;
      if (el && typeof el.focus === "function") {
        queueMicrotask(() => {
          try {
            el.focus();
          } catch {
            /* ignore — element may have been unmounted */
          }
        });
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg" data-testid="shortcut-help-overlay">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            Press ? at any time to show this overlay.
          </DialogDescription>
        </DialogHeader>
        {grouped.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No keyboard shortcuts registered.
          </p>
        ) : (
          <div className="space-y-4">
            {grouped.map(({ group, items }) => (
              <div key={group}>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {group}
                </h3>
                <ul className="space-y-1">
                  {items.map((s) => (
                    <li
                      key={s.id}
                      className="flex items-center justify-between gap-4 text-sm"
                    >
                      <span>{s.description}</span>
                      <kbd className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded border border-border bg-muted px-2 font-mono text-xs">
                        {s.key.toUpperCase()}
                      </kbd>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
